import { defineStore } from 'pinia';
import { ref, computed, nextTick } from 'vue';
import type {
  Subscription,
  SubscriptionLimits,
  StripePlan,
  SubscriptionStatusResponse,
  SubscriptionPlansResponse,
  CreateCheckoutSessionResponse,
  TrialInfo,
  ConversionInfo,
  GracePeriodInfo,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  ConvertToSubscriptionResponse
} from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';
import { apiClient, createAuthenticatedApiClient } from '../services/ApiClient';
import { useAuth } from '../composables/useAuth';

const API_URL = '/api/subscriptions';

// Time constants
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

// Create an authenticated API client that will be initialized with auth token
const authenticatedApiClient = createAuthenticatedApiClient();

export const useSubscriptionStore = defineStore('subscription', () => {
  const subscription = ref<Subscription>({
    email: '',
    stripeCustomerId: null,
    subscriptionId: null,
    status: 'free',
    currentPeriodEnd: null,
    planType: 'free',
    updatedAt: Date.now(),
  });

  const limits = ref<SubscriptionLimits>({
    maxPlaylists: 1,
    maxSongsPerPlaylist: 3,
    canAccessAllPlaylists: false,
  });

  const expiringSoon = ref(false);
  const loading = ref(true); // Start as loading until first subscription fetch completes
  const loadingPlans = ref(false);
  const loadingCheckout = ref(false);
  const error = ref<string | null>(null);

  // Trial system state
  const trial = ref<TrialInfo>({
    isInTrial: false,
    daysRemaining: null,
    startDate: null,
    endDate: null,
  });

  const conversions = ref<ConversionInfo>({
    showConvertPrompt: false,
    showUpgradePrompt: false,
    promptMessage: null,
    daysUntilExpiry: null,
  });

  const gracePeriod = ref<GracePeriodInfo>({
    isInGracePeriod: false,
    reason: null,
    daysRemaining: null,
  });

  // Cached subscription plans (session-scoped, cleared on page refresh)
  const cachedPlans = ref<StripePlan[] | null>(null);
  // Loading promise to prevent concurrent API calls
  let fetchPlansPromise: Promise<{ plans: StripePlan[] }> | null = null;

  // Computed properties
  const hasActiveSubscription = computed(() => {
    return subscription.value.status === 'active' &&
           subscription.value.currentPeriodEnd &&
           subscription.value.currentPeriodEnd > Date.now();
  });

  const canAddPlaylist = computed(() => {
    return limits.value.maxPlaylists === -1 || limits.value.maxPlaylists > 0;
  });

  const canAddSong = computed(() => {
    return limits.value.maxSongsPerPlaylist === -1 || limits.value.maxSongsPerPlaylist > 0;
  });

  const daysUntilExpiry = computed(() => {
    if (!subscription.value.currentPeriodEnd) {
      return null;
    }
    const endDate = new Date(subscription.value.currentPeriodEnd).getTime();
    const days = Math.floor((endDate - Date.now()) / MILLISECONDS_PER_DAY);
    return days;
  });

  // Actions
  async function fetchSubscriptionStatus(): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      // Set up authentication token provider
      const auth = useAuth();
      authenticatedApiClient.setTokenProvider(auth.getToken);

      const response = await authenticatedApiClient.get<SubscriptionStatusResponse>(
        `${API_URL}/status`,
        {
          credentials: 'include',
          description: 'Fetch subscription status'
        }
      );

      if ('subscription' in response) {
        subscription.value = response.subscription;
        limits.value = response.limits;
        expiringSoon.value = response.expiringSoon;

        // Update trial system fields
        trial.value = response.trial;
        conversions.value = response.conversions;
        gracePeriod.value = response.gracePeriod;

        // Wait for Vue to process reactive updates before clearing loading state
        await nextTick();
      } else {
        error.value = response.message || 'Failed to fetch subscription status';
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred';
    } finally {
      loading.value = false;
    }
  }

  async function fetchPlans(): Promise<{ plans: StripePlan[] }> {
    // Return cached plans if available
    if (cachedPlans.value) {
      logger.debug('[subscriptionStore] Using cached plans');
      return { plans: cachedPlans.value };
    }

    // If a fetch is already in progress, wait for it to complete
    if (fetchPlansPromise) {
      logger.debug('[subscriptionStore] Waiting for in-flight plans request');
      return fetchPlansPromise;
    }

    // Start new fetch and store the promise
    loadingPlans.value = true;
    error.value = null;

    fetchPlansPromise = (async (): Promise<{ plans: StripePlan[] }> => {
      try {
        logger.debug('[subscriptionStore] Fetching plans from API');
        const response = await apiClient.get<SubscriptionPlansResponse>(
          `${API_URL}/plans`,
          { description: 'Fetch subscription plans' }
        );

        loadingPlans.value = false;

        if ('plans' in response) {
          // Cache the fetched plans
          cachedPlans.value = response.plans;
          return { plans: response.plans };
        }

        error.value = response.message || 'Failed to fetch subscription plans';
        return { plans: [] };
      } catch (err) {
        loadingPlans.value = false;
        const errorMessage = err instanceof Error ? err.message : 'Failed to load subscription plans';
        error.value = errorMessage;
        return { plans: [] };
      } finally {
        // Clear the promise once complete so future calls work correctly
        fetchPlansPromise = null;
      }
    })();

    return fetchPlansPromise;
  }

  async function createCheckoutSession(priceId: string): Promise<string | null> {
    loadingCheckout.value = true;
    error.value = null;

    // Set up authentication token provider
    const auth = useAuth();
    authenticatedApiClient.setTokenProvider(auth.getToken);

    const response = await authenticatedApiClient.post<CreateCheckoutSessionResponse>(
      `${API_URL}/create-checkout-session`,
      { priceId },
      {
        credentials: 'include',
        description: 'Create checkout session'
      }
    );

    loadingCheckout.value = false;

    if ('url' in response) {
      return response.url;
    }

    error.value = response.message || 'Failed to create checkout session';
    return null;
  }

  async function cancelSubscription(reason?: string): Promise<boolean> {
    loadingCheckout.value = true;
    error.value = null;

    try {
      // Set up authentication token provider
      const auth = useAuth();
      authenticatedApiClient.setTokenProvider(auth.getToken);

      const response = await authenticatedApiClient.post<CancelSubscriptionResponse>(
        `${API_URL}/cancel`,
        { reason },
        {
          credentials: 'include',
          description: 'Cancel subscription'
        }
      );

      loadingCheckout.value = false;

      if ('success' in response && response.success) {
        // Refresh subscription status to get updated data
        await fetchSubscriptionStatus();
        return true;
      }

      error.value = response.message || 'Failed to cancel subscription';
      return false;
    } catch (err) {
      loadingCheckout.value = false;
      error.value = err instanceof Error ? err.message : 'Failed to cancel subscription';
      return false;
    }
  }

  async function reactivateSubscription(): Promise<boolean> {
    loadingCheckout.value = true;
    error.value = null;

    try {
      // Set up authentication token provider
      const auth = useAuth();
      authenticatedApiClient.setTokenProvider(auth.getToken);

      const response = await authenticatedApiClient.post<ReactivateSubscriptionResponse>(
        `${API_URL}/reactivate`,
        {},
        {
          credentials: 'include',
          description: 'Reactivate subscription'
        }
      );

      loadingCheckout.value = false;

      if ('success' in response && response.success) {
        // Refresh subscription status to get updated data
        await fetchSubscriptionStatus();
        return true;
      }

      error.value = response.message || 'Failed to reactivate subscription';
      return false;
    } catch (err) {
      loadingCheckout.value = false;
      error.value = err instanceof Error ? err.message : 'Failed to reactivate subscription';
      return false;
    }
  }

  async function convertToSubscription(priceId: string): Promise<string | null> {
    loadingCheckout.value = true;
    error.value = null;

    try {
      // Set up authentication token provider
      const auth = useAuth();
      authenticatedApiClient.setTokenProvider(auth.getToken);

      const response = await authenticatedApiClient.post<ConvertToSubscriptionResponse>(
        `${API_URL}/convert-to-subscription`,
        { priceId },
        {
          credentials: 'include',
          description: 'Convert to recurring subscription'
        }
      );

      loadingCheckout.value = false;

      if ('success' in response && response.success) {
        return response.sessionUrl;
      }

      error.value = response.message || 'Failed to convert to subscription';
      return null;
    } catch (err) {
      loadingCheckout.value = false;
      error.value = err instanceof Error ? err.message : 'Failed to convert to subscription';
      return null;
    }
  }

  /**
   * Clear cached plans and force fresh fetch on next call
   * Useful for testing or when plans are updated
   */
  function clearPlansCache(): void {
    logger.debug('[subscriptionStore] Clearing plans cache');
    cachedPlans.value = null;
    fetchPlansPromise = null;
  }

  function checkPlaylistLimit(currentCount: number): boolean {
    if (limits.value.maxPlaylists === -1) {
      return true;
    }
    return currentCount < limits.value.maxPlaylists;
  }

  function checkSongLimit(currentCount: number): boolean {
    if (limits.value.maxSongsPerPlaylist === -1) {
      return true;
    }
    return currentCount < limits.value.maxSongsPerPlaylist;
  }

  return {
    subscription,
    limits,
    expiringSoon,
    loading,
    loadingPlans,
    loadingCheckout,
    error,

    // Trial system state
    trial,
    conversions,
    gracePeriod,

    // Computed properties
    hasActiveSubscription,
    canAddPlaylist,
    canAddSong,
    daysUntilExpiry,

    // Actions
    fetchSubscriptionStatus,
    fetchPlans,
    createCheckoutSession,
    cancelSubscription,
    reactivateSubscription,
    convertToSubscription,
    clearPlansCache,
    checkPlaylistLimit,
    checkSongLimit,
  };
});
